import { ChangeEvent, useState } from 'react';
import { CoreDialog, CoreDialogActions, CoreBox, CoreCheckbox, CoreTypography } from '@youscience/core';
import { carnegieGroups } from '@common-types';

import useHomePageStore from '@store/HomePageStore';
import useSchoolSelectionStore from '@store/SchoolSelectionStore';
import useUserInfoStore from '@store/UserInfoStore';
import useNotificationStore from '@store/NotificationStore';

import useUserPreferencesStore from '@store/UserPreferencesStore';

import { getEnrollmentSize, getSchoolType } from '@utils/schoolInfographics';

import { ReducedSchoolCard } from '@components/ReducedSchoolCard';

import { sxStyles, StyledDialogContent } from './PartnerSchoolDialog.styles';
import ActionButton from '@components/ActionButton';

interface SuccessDialogProps {
  isOpenDialog: boolean;
  onCloseDialog: () => void;
}

export const PartnerSchoolDialog = (props: SuccessDialogProps) => {
  const { isOpenDialog, onCloseDialog } = props;

  const { cardInfo } = useHomePageStore((state) => state.landingPageSchoolData);

  const {
    grantAccessToYSProfile,
    schoolSelectionData: { enrolledSchool },
  } = useSchoolSelectionStore((state) => state);

  const { setSchoolSharingNotificationRead } = useUserInfoStore((state) => state);

  const { enrolledSchoolId, tenantSharing } = useUserPreferencesStore((state) => state.userPreferences);

  const schoolHasAccessToProfile = tenantSharing?.find((item) => item.collegeId === enrolledSchoolId)?.currentlySharing;
  const [isProvideAccess, setIsProvideAccess] = useState<boolean>(schoolHasAccessToProfile ?? false);

  const costPerYear = +cardInfo.cost === 0 ? '' : `${cardInfo.cost} AVG`;
  const schoolArrayType = carnegieGroups.get(cardInfo.schoolLength) as number[];
  const schoolAddress = `${cardInfo.address}. – ${cardInfo.city}, ${cardInfo.state}, US`;
  const schoolType = getSchoolType(schoolArrayType[0]);
  const enrollmentSize = getEnrollmentSize(cardInfo.totalStudentsEnrolled);

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsProvideAccess(checked);
  };

  const handleRemindMeLaterClick = () => {
    useNotificationStore.getState().notify({
      title: 'We will remind you about this next time.',
      message: '',
      severity: 'info',
    });

    onCloseDialog();
  };

  const handleCloseClick = () => {
    setSchoolSharingNotificationRead();

    onCloseDialog();
  };

  const handleConfirmClick = async () => {
    if (isProvideAccess) {
      await grantAccessToYSProfile(cardInfo, Boolean(isProvideAccess));
    }

    handleCloseClick();
  };

  return (
    <CoreDialog sx={sxStyles.dialogWrap} open={isOpenDialog}>
      <StyledDialogContent>
        <CoreTypography data-testid="dialogTitle" sx={sxStyles.title} variant="h4" component="h3">
          {enrolledSchool?.currentlySharing ? 'You are now sharing your profile' : 'Good news!'}
        </CoreTypography>

        <CoreTypography data-testid="dialogDescription" sx={sxStyles.description}>
          {cardInfo.name} is now part of our Brightpath Platform, and you{' '}
          {enrolledSchool?.currentlySharing ? 'are sharing' : 'can share'} your YouScience profile with them. You can
          change your sharing settings on your profile anytime.
        </CoreTypography>

        <ReducedSchoolCard
          title={cardInfo.name}
          pictureUrl={cardInfo.pictureUrl || ''}
          schoolType={schoolType}
          enrollmentSize={enrollmentSize}
          costPerYear={costPerYear}
          address={schoolAddress}
          isHideActions
        />

        {!enrolledSchool?.currentlySharing ? (
          <CoreBox sx={sxStyles.checkBox}>
            <CoreCheckbox
              data-testid="grantAccessToYSProfile"
              checked={isProvideAccess}
              value="isGrantAccessToYSProfile"
              onChange={handleCheckBoxChange}
            />

            <CoreTypography data-testid="grantAccessToYSProfileText" sx={sxStyles.checkBoxTitle} variant="body2">
              {`Please grant ${cardInfo.name} access to my YouScience profile. I understand that employees
            of ${cardInfo.name} will have access to my personal YouScience profile to use in providing me
            academic and career counseling.`}
            </CoreTypography>
          </CoreBox>
        ) : null}
      </StyledDialogContent>

      <CoreDialogActions sx={sxStyles.footer}>
        <ActionButton
          size="large"
          variant="outlined"
          data-testid="remindMeLaterButton"
          onClick={handleRemindMeLaterClick}
        >
          Remind me later"
        </ActionButton>
        {enrolledSchool?.currentlySharing ? (
          <ActionButton size="large" data-testid="closeButton" onClick={handleCloseClick}>
            Close
          </ActionButton>
        ) : (
          <ActionButton size="large" data-testid="confirmButton" onClick={handleConfirmClick} busyText="Saving">
            Confirm
          </ActionButton>
        )}
      </CoreDialogActions>
    </CoreDialog>
  );
};
